/**
 * AudioSystem — procedural audio using the Web Audio API.
 *
 * All sounds are synthesised at runtime (no audio file assets required).
 * Wire to Phaser's AudioContext via `(scene.sound as any).context`.
 *
 * IMPORTANT: The AudioContext must be unlocked before playback.
 * Call `AudioSystem.resume()` (or let it auto-resume) on first user gesture.
 */
export class AudioSystem {
  private ctx: AudioContext;
  private masterGain: GainNode;

  constructor(scene: Phaser.Scene) {
    // Access Phaser's shared WebAudio context
    this.ctx = (scene.sound as any).context as AudioContext;

    // Master gain — allows muting / volume control
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 0.6;
    this.masterGain.connect(this.ctx.destination);
  }

  /**
   * Resume AudioContext after user gesture.
   * Call this inside any pointer/key handler to unlock audio.
   */
  resume(): void {
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => { /* swallow */ });
    }
  }

  // ── Gunshot ────────────────────────────────────────────────────────────────
  /**
   * Short white-noise burst with quick gain envelope (80ms).
   */
  playGunshot(): void {
    if (!this.ready()) return;
    const bufferSize = Math.floor(this.ctx.sampleRate * 0.08);
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * 0.8;
    }

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;

    // High-pass to make it crack-like
    const hpf = this.ctx.createBiquadFilter();
    hpf.type = 'highpass';
    hpf.frequency.value = 800;

    const gain = this.ctx.createGain();
    const now = this.ctx.currentTime;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.7, now + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);

    source.connect(hpf);
    hpf.connect(gain);
    gain.connect(this.masterGain);
    source.start(now);
  }

  // ── Explosion ─────────────────────────────────────────────────────────────
  /**
   * Low-frequency noise + oscillator sweep from 80 Hz → 20 Hz over 400ms.
   */
  playExplosion(): void {
    if (!this.ready()) return;
    const now = this.ctx.currentTime;

    // Low-freq noise
    const bufferSize = Math.floor(this.ctx.sampleRate * 0.4);
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1);
    }

    const noiseSource = this.ctx.createBufferSource();
    noiseSource.buffer = buffer;

    // Low-pass filter to keep only rumble
    const lpf = this.ctx.createBiquadFilter();
    lpf.type = 'lowpass';
    lpf.frequency.value = 200;

    const noiseGain = this.ctx.createGain();
    noiseGain.gain.setValueAtTime(0, now);
    noiseGain.gain.linearRampToValueAtTime(1.0, now + 0.02);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);

    noiseSource.connect(lpf);
    lpf.connect(noiseGain);
    noiseGain.connect(this.masterGain);
    noiseSource.start(now);

    // Oscillator sweep 80 → 20 Hz
    const osc = this.ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(80, now);
    osc.frequency.exponentialRampToValueAtTime(20, now + 0.4);

    const oscGain = this.ctx.createGain();
    oscGain.gain.setValueAtTime(0, now);
    oscGain.gain.linearRampToValueAtTime(0.5, now + 0.02);
    oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);

    osc.connect(oscGain);
    oscGain.connect(this.masterGain);
    osc.start(now);
    osc.stop(now + 0.41);
  }

  // ── Rocket launch ─────────────────────────────────────────────────────────
  /**
   * Rising tone from 120 Hz → 400 Hz over 300ms.
   */
  playRocketLaunch(): void {
    if (!this.ready()) return;
    const now = this.ctx.currentTime;

    const osc = this.ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(120, now);
    osc.frequency.exponentialRampToValueAtTime(400, now + 0.3);

    // Noise layer for whoosh
    const bufferSize = Math.floor(this.ctx.sampleRate * 0.3);
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const noiseSource = this.ctx.createBufferSource();
    noiseSource.buffer = buffer;

    const hpf = this.ctx.createBiquadFilter();
    hpf.type = 'bandpass';
    hpf.frequency.value = 300;
    hpf.Q.value = 0.5;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.4, now + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);

    const oscGain = this.ctx.createGain();
    oscGain.gain.setValueAtTime(0, now);
    oscGain.gain.linearRampToValueAtTime(0.25, now + 0.05);
    oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);

    noiseSource.connect(hpf);
    hpf.connect(gain);
    gain.connect(this.masterGain);

    osc.connect(oscGain);
    oscGain.connect(this.masterGain);

    noiseSource.start(now);
    osc.start(now);
    osc.stop(now + 0.31);
  }

  // ── Soldier death ─────────────────────────────────────────────────────────
  /**
   * Brief descending tone 300 Hz → 80 Hz over 200ms.
   */
  playSoldierDeath(): void {
    if (!this.ready()) return;
    const now = this.ctx.currentTime;

    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(300, now);
    osc.frequency.exponentialRampToValueAtTime(80, now + 0.2);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.3, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);

    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.start(now);
    osc.stop(now + 0.21);
  }

  // ── Mission complete ──────────────────────────────────────────────────────
  /**
   * Ascending major arpeggio: C4 → E4 → G4 → C5 (sine).
   */
  playMissionComplete(): void {
    if (!this.ready()) return;
    const now = this.ctx.currentTime;
    // C4=261.63, E4=329.63, G4=392.00, C5=523.25
    const notes = [261.63, 329.63, 392.0, 523.25];
    const noteDuration = 0.18;
    const noteGap = 0.20;

    notes.forEach((freq, i) => {
      const t = now + i * noteGap;
      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;

      const gain = this.ctx.createGain();
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.35, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, t + noteDuration);

      osc.connect(gain);
      gain.connect(this.masterGain);
      osc.start(t);
      osc.stop(t + noteDuration + 0.01);
    });
  }

  // ── Mission fail ──────────────────────────────────────────────────────────
  /**
   * Descending minor tones: A4 → F4 → D4 (sine).
   */
  playMissionFail(): void {
    if (!this.ready()) return;
    const now = this.ctx.currentTime;
    // A4=440, F4=349.23, D4=293.66
    const notes = [440, 349.23, 293.66];
    const noteDuration = 0.22;
    const noteGap = 0.24;

    notes.forEach((freq, i) => {
      const t = now + i * noteGap;
      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;

      const gain = this.ctx.createGain();
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.3, t + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, t + noteDuration);

      osc.connect(gain);
      gain.connect(this.masterGain);
      osc.start(t);
      osc.stop(t + noteDuration + 0.01);
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private ready(): boolean {
    return this.ctx.state !== 'closed';
  }
}
